// ─────────────────────────────────────────────
// KLOTHIQ — Bulk Import Parsing & Validation
// Pure(ish) functions: parse an uploaded .xlsx, validate every row
// against the org's REAL Master Config (never the hardcoded
// DEFAULT_* fallback lists — spec FEAT-BULK-IMPORT-001 FR-010),
// detect matches against existing products/variants, and produce
// the exact payload commit_bulk_product_import expects. Nothing
// here writes to the database — see useBulkImport.ts for the
// single commit call.
// ─────────────────────────────────────────────

import * as XLSX from 'xlsx';
import type { JerseySkuReview, JerseyDetailsV2, JerseyOutputMode } from '@klothiq/domain';
import { supabase } from '@/lib/supabase';
import {
  buildImportColumns,
  normalizeHeaderLabel,
  sheetNameForType,
  ImportColumn,
} from './bulkImportColumns';
import {
  buildProductCodePrefix,
  formatProductCode,
  buildVariantSku,
  getAbbreviation,
  COLOR_CODES,
} from './productCodeUtils';

// ── Public types ────────────────────────────────────────────────

export interface RowError {
  row: number; // 1-based spreadsheet row number (header row = 1)
  column?: string;
  message: string;
}

export interface RowWarning {
  row: number;
  message: string;
}

export interface ValidatedVariant {
  match: 'new' | 'update';
  existing_id?: string;
  sku: string;
  size: string;
  color?: string;
  default_cost: number;
  default_price: number;
  low_stock_level: number;
  attributes: Record<string, unknown>;
  opening_stock: number;
  sourceRow: number;
  ignoredOpeningStock: boolean; // EC-008 — typed but will not be applied
}

export interface FieldDiff {
  field: string;
  oldValue: string;
  newValue: string;
}

export interface ValidatedProductGroup {
  group_key: string;
  jersey_review?: JerseySkuReview;
  jersey_entry?: { details?: JerseyDetailsV2; name_mode?: JerseyOutputMode; code_mode?: JerseyOutputMode; sku_modes?: Record<number, JerseyOutputMode> };
  match: 'new' | 'update';
  existing_id?: string;
  expected_updated_at?: string;
  product_code: string;
  name: string;
  category_id?: string;
  target_audience_id?: string;
  age_group_id?: string;
  description?: string;
  attributes: Record<string, unknown>;
  variants: ValidatedVariant[];
  diffs: FieldDiff[]; // populated only when match === 'update'
  isInactiveExisting: boolean; // EC-005
  sourceRows: number[];
}

export interface ValidationResult {
  columns: ImportColumn[];
  groups: ValidatedProductGroup[];
  errors: RowError[];
  warnings: RowWarning[];
  totalDataRows: number;
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`)
      .join(',')}}`;
  return JSON.stringify(value) ?? 'null';
}
async function importGroupKey(group: ValidatedProductGroup): Promise<string> {
  const identity = stableJson({
    existing_id: group.existing_id || null,
    name: group.name,
    attributes: group.attributes,
    variants: group.variants
      .map((v) => [v.existing_id || '', v.sku, v.color || '', v.size, v.attributes])
      .sort((a, b) => stableJson(a).localeCompare(stableJson(b))),
  });
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(identity));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export interface ParsedWorkbook {
  headerIndex: Map<string, number>; // normalized label -> column index
  rows: string[][];
}

// ── Step 1: parse the uploaded file ─────────────────────────────

export async function parseWorkbook(
  file: File,
  productTypeCode: string,
  columns: ImportColumn[],
): Promise<ParsedWorkbook> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });

  // Prefer the exact expected sheet name (the untouched downloaded template).
  // If it's missing — very commonly because the file was re-saved from a
  // copy/paste into a fresh spreadsheet, which resets the tab to "Sheet1" —
  // fall back to whichever single non-Instructions sheet is present. Only
  // error when that's genuinely ambiguous (0 or 2+ candidates), since that's
  // the actual "wrong file" signal, not the tab's name by itself.
  const expectedSheet = sheetNameForType(productTypeCode);
  let resolvedSheetName = wb.SheetNames.includes(expectedSheet) ? expectedSheet : undefined;

  if (!resolvedSheetName) {
    const candidates = wb.SheetNames.filter((n) => n.trim().toLowerCase() !== 'instructions');
    if (candidates.length === 1) {
      resolvedSheetName = candidates[0];
    } else if (candidates.length === 0) {
      throw new Error(`This file has no data sheet (found sheets: ${wb.SheetNames.join(', ')}).`);
    } else {
      throw new Error(
        `Could not tell which sheet holds your data — found multiple candidates ` +
          `(${candidates.join(', ')}) and none is named "${expectedSheet}". ` +
          `Rename the sheet with your product rows to "${expectedSheet}" and re-upload.`,
      );
    }
  }

  const ws = wb.Sheets[resolvedSheetName!];
  if (!ws) {
    throw new Error(`Could not read the "${resolvedSheetName}" sheet from this file.`);
  }
  const aoa = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' });
  if (aoa.length === 0) {
    throw new Error('The uploaded sheet has no header row.');
  }

  const headerRow = (aoa[0] ?? []).map((h) => String(h ?? ''));
  const headerIndex = new Map<string, number>();
  headerRow.forEach((h, idx) => {
    const norm = normalizeHeaderLabel(h);
    if (norm) headerIndex.set(norm, idx);
  });

  // Missing-required-column is a whole-file error (EC-003), not per-row.
  const missing = columns.filter(
    (c) => c.required && !headerIndex.has(normalizeHeaderLabel(c.label)),
  );
  if (missing.length > 0) {
    throw new Error(
      `This file is missing required column(s): ${missing.map((c) => c.label).join(', ')}.`,
    );
  }

  const dataRows = aoa
    .slice(1)
    .map((r) => (r ?? []).map((v) => String(v ?? '').trim()))
    .filter((r) => r.some((cell) => cell !== ''));

  return { headerIndex, rows: dataRows };
}

function cell(parsed: ParsedWorkbook, row: string[], col: ImportColumn): string {
  const idx = parsed.headerIndex.get(normalizeHeaderLabel(col.label));
  if (idx === undefined) return '';
  return (row[idx] ?? '').trim();
}

// ── Step 2: fetch org-real Master Config + existing records ────

interface MasterConfigSnapshot {
  categories: { id: string; name: string; parent_id: string | null }[];
  targetAudiences: { id: string; name: string }[];
  ageGroups: { id: string; name: string }[];
  teams: { id: string; name: string }[];
}

async function fetchMasterConfigSnapshot(orgId: string): Promise<MasterConfigSnapshot> {
  const safe = async <T>(p: PromiseLike<{ data: T[] | null }>): Promise<T[]> => {
    try {
      const { data } = await p;
      return data ?? [];
    } catch {
      return [];
    }
  };

  const [categories, targetAudiences, ageGroups, teams] = await Promise.all([
    safe(
      supabase
        .from('categories')
        .select('id, name, parent_id')
        .eq('organization_id', orgId)
        .eq('is_active', true),
    ),
    safe(
      supabase
        .from('target_audiences')
        .select('id, name')
        .eq('organization_id', orgId)
        .eq('is_active', true),
    ),
    safe(
      supabase
        .from('age_groups')
        .select('id, name')
        .eq('organization_id', orgId)
        .eq('is_active', true),
    ),
    safe(
      supabase.from('teams').select('id, name').eq('organization_id', orgId).eq('is_active', true),
    ),
  ]);

  return { categories, targetAudiences, ageGroups, teams } as MasterConfigSnapshot;
}

interface ExistingProduct {
  updated_at: string;
  id: string;
  product_code: string;
  name: string;
  category_id: string | null;
  target_audience_id: string | null;
  age_group_id: string | null;
  description: string | null;
  attributes: Record<string, unknown> | null;
  is_active: boolean;
}

interface ExistingVariant {
  id: string;
  sku: string;
  size: string;
  color: string | null;
  default_cost: number;
  default_price: number;
  low_stock_level: number;
  attributes: Record<string, unknown> | null;
  product_id: string;
}

async function fetchExisting(
  orgId: string,
  codes: string[],
  skus: string[],
): Promise<{ products: ExistingProduct[]; variants: ExistingVariant[] }> {
  const products: ExistingProduct[] = [];
  const variants: ExistingVariant[] = [];
  for (let start = 0; start < codes.length; start += 100) {
    const { data, error } = await supabase
      .from('products')
      .select(
        'id, updated_at, product_code, name, category_id, target_audience_id, age_group_id, description, attributes, is_active',
      )
      .eq('organization_id', orgId)
      .in('product_code', codes.slice(start, start + 100));
    if (error) throw error;
    products.push(...((data || []) as ExistingProduct[]));
  }
  for (let start = 0; start < skus.length; start += 100) {
    const { data, error } = await supabase
      .from('product_variants')
      .select(
        'id, sku, size, color, default_cost, default_price, low_stock_level, attributes, product_id',
      )
      .eq('organization_id', orgId)
      .in('sku', skus.slice(start, start + 100));
    if (error) throw error;
    variants.push(...((data || []) as ExistingVariant[]));
  }

  return { products: products as ExistingProduct[], variants: variants as ExistingVariant[] };
}

// ── Case-insensitive/trimmed exact matcher (spec UNK-004: no fuzzy matching) ──

// Collapses whitespace/case and a single trailing "s" so "Adult" matches a
// configured "Adults" (and vice versa) without treating them as ambiguous.
function looseNameKey(name: string): string {
  const base = name.trim().toLowerCase().replace(/\s+/g, ' ');
  return base.length > 1 && base.endsWith('s') ? base.slice(0, -1) : base;
}

function matchByName<T extends { name: string }>(
  value: string,
  options: T[],
): { match?: T; ambiguous: boolean } {
  const norm = value.trim().toLowerCase();
  const hits = options.filter((o) => o.name.trim().toLowerCase() === norm);
  if (hits.length === 1) return { match: hits[0], ambiguous: false };
  if (hits.length > 1) return { ambiguous: true };

  // No exact hit — fall back to a singular/plural-insensitive match (e.g. a
  // row typed "Adult" against a configured "Adults") rather than hard-failing
  // on what is usually just a spreadsheet/Master-Config naming mismatch.
  const loose = looseNameKey(value);
  const looseHits = options.filter((o) => looseNameKey(o.name) === loose);
  if (looseHits.length === 1) return { match: looseHits[0], ambiguous: false };
  if (looseHits.length > 1) return { ambiguous: true };

  return { ambiguous: false };
}

// ── Row-level parsed shape (before grouping) ────────────────────

interface ParsedRow {
  rowNum: number;
  product_code: string;
  name: string;
  category: string;
  target_audience: string;
  age_group: string;
  description: string;
  productAttrs: Record<string, string>;
  size: string;
  color: string;
  sku: string;
  default_cost: string;
  default_price: string;
  opening_stock: string;
  low_stock_level: string;
  variantAttrs: Record<string, string>;
}

function extractRow(
  parsed: ParsedWorkbook,
  row: string[],
  rowNum: number,
  columns: ImportColumn[],
): ParsedRow {
  const productAttrs: Record<string, string> = {};
  const variantAttrs: Record<string, string> = {};
  for (const col of columns) {
    if (col.kind !== 'attribute') continue;
    const v = cell(parsed, row, col);
    if (col.level === 'product') productAttrs[col.attributeCode!] = v;
    else variantAttrs[col.attributeCode!] = v;
  }

  const get = (key: string) => {
    const col = columns.find((c) => c.key === key);
    return col ? cell(parsed, row, col) : '';
  };

  return {
    rowNum,
    product_code: get('product_code').toUpperCase(),
    name: get('name'),
    category: get('category'),
    target_audience: get('target_audience'),
    age_group: get('age_group'),
    description: get('description'),
    productAttrs,
    size: get('size'),
    color: get('color'),
    sku: get('sku').toUpperCase(),
    default_cost: get('default_cost'),
    default_price: get('default_price'),
    opening_stock: get('opening_stock'),
    low_stock_level: get('low_stock_level'),
    variantAttrs,
  };
}

/** Content key used to group code-less rows that describe the same new product (FR-004). */
function productContentKey(r: ParsedRow): string {
  return JSON.stringify([
    r.name.trim().toLowerCase(),
    r.category.trim().toLowerCase(),
    r.target_audience.trim().toLowerCase(),
    r.age_group.trim().toLowerCase(),
    r.description.trim().toLowerCase(),
    Object.entries(r.productAttrs)
      .sort()
      .map(([k, v]) => `${k}=${v.trim().toLowerCase()}`)
      .join('|'),
  ]);
}

function parseNonNegNumber(raw: string): number | null {
  if (raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function parseNonNegInt(raw: string): number | null {
  if (raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return null;
  return n;
}

// ── Step 3: the main entry point ────────────────────────────────

export async function validateImportFile(
  file: File,
  productType: { id: string; name: string; code: string },
  dynamicAttributes: Array<{
    code?: string | null;
    name: string;
    is_variant_level: boolean;
    is_required?: boolean;
    input_type: string;
    options?: unknown;
  }>,
  orgId: string,
  options?: { jerseyIdentifiers?: boolean },
): Promise<ValidationResult> {
  const columns = buildImportColumns(dynamicAttributes as never);
  const parsed = await parseWorkbook(file, productType.code, columns);

  const errors: RowError[] = [];
  const warnings: RowWarning[] = [];
  const parsedRows = parsed.rows.map((row, i) => extractRow(parsed, row, i + 2, columns)); // +2: header is row 1

  const config = await fetchMasterConfigSnapshot(orgId);

  const typedCodes = Array.from(new Set(parsedRows.map((r) => r.product_code).filter(Boolean)));
  const typedSkus = Array.from(new Set(parsedRows.map((r) => r.sku).filter(Boolean)));
  const { products: existingProducts, variants: existingVariants } = await fetchExisting(
    orgId,
    typedCodes,
    typedSkus,
  );
  const existingByCode = new Map(existingProducts.map((p) => [p.product_code.toUpperCase(), p]));
  const existingVariantBySku = new Map(existingVariants.map((v) => [v.sku.toUpperCase(), v]));

  // ── Group rows into products ──
  interface Group {
    key: string;
    rows: ParsedRow[];
    typedCode: string; // '' if none
  }
  const groupsByCode = new Map<string, Group>();
  const groupsByContent = new Map<string, Group>();
  const orderedGroups: Group[] = [];

  for (const r of parsedRows) {
    let group: Group | undefined;
    if (r.product_code) {
      group = groupsByCode.get(r.product_code);
      if (!group) {
        group = { key: r.product_code, rows: [], typedCode: r.product_code };
        groupsByCode.set(r.product_code, group);
        orderedGroups.push(group);
      }
    } else {
      const contentKey = productContentKey(r);
      group = groupsByContent.get(contentKey);
      if (!group) {
        group = { key: contentKey, rows: [], typedCode: '' };
        groupsByContent.set(contentKey, group);
        orderedGroups.push(group);
      }
    }
    group.rows.push(r);
  }

  // ── Duplicate SKU within file (typed SKUs only — EC-004) ──
  const skuCount = new Map<string, number>();
  parsedRows.forEach((r) => {
    if (r.sku) skuCount.set(r.sku, (skuCount.get(r.sku) ?? 0) + 1);
  });

  // ── Resolve each group ──
  const validGroups: ValidatedProductGroup[] = [];
  const newGroupsNeedingCode: { group: ValidatedProductGroup; prefixInput: ParsedRow }[] = [];

  for (const g of orderedGroups) {
    const first = g.rows[0]!;
    const groupErrors: RowError[] = [];
    let category_id: string | undefined;
    let target_audience_id: string | undefined;
    let age_group_id: string | undefined;

    if (!first.name.trim()) {
      g.rows.forEach((r) =>
        groupErrors.push({
          row: r.rowNum,
          column: 'Product Name',
          message: 'Product Name is required.',
        }),
      );
    }

    if (first.category.trim()) {
      const { match, ambiguous } = matchByName(first.category, config.categories);
      if (ambiguous) {
        groupErrors.push({
          row: first.rowNum,
          column: 'Category',
          message: `"${first.category}" matches more than one category (nested categories share this name) — use a unique name or leave blank.`,
        });
      } else if (!match) {
        groupErrors.push({
          row: first.rowNum,
          column: 'Category',
          message: `"${first.category}" is not a category configured for this organization. Valid options: ${config.categories.map((c) => c.name).join(', ') || '(none configured)'}.`,
        });
      } else {
        category_id = match.id;
      }
    }

    if (first.target_audience.trim()) {
      const { match, ambiguous } = matchByName(first.target_audience, config.targetAudiences);
      if (ambiguous || !match) {
        groupErrors.push({
          row: first.rowNum,
          column: 'Target Audience',
          message: `"${first.target_audience}" is not a valid Target Audience. Valid options: ${config.targetAudiences.map((a) => a.name).join(', ') || '(none configured)'}.`,
        });
      } else {
        target_audience_id = match.id;
      }
    }

    if (first.age_group.trim()) {
      const { match, ambiguous } = matchByName(first.age_group, config.ageGroups);
      if (ambiguous || !match) {
        groupErrors.push({
          row: first.rowNum,
          column: 'Age Group',
          message: `"${first.age_group}" is not a valid Age Group. Valid options: ${config.ageGroups.map((a) => a.name).join(', ') || '(none configured)'}.`,
        });
      } else {
        age_group_id = match.id;
      }
    }

    const productAttrColumns = columns.filter(
      (c) => c.kind === 'attribute' && c.level === 'product',
    );
    const resolvedProductAttrs: Record<string, string> = {};
    for (const col of productAttrColumns) {
      const raw = (first.productAttrs[col.attributeCode!] ?? '').trim();
      if (!raw) {
        if (col.required)
          groupErrors.push({
            row: first.rowNum,
            column: col.label,
            message: `"${col.label}" is required for this product type.`,
          });
        continue;
      }
      if (col.optionsSource === 'teams') {
        const { match, ambiguous } = matchByName(raw, config.teams);
        if (ambiguous || !match) {
          groupErrors.push({
            row: first.rowNum,
            column: col.label,
            message: `"${raw}" is not a valid Team. Valid options: ${config.teams.map((t) => t.name).join(', ') || '(none configured)'}.`,
          });
        } else {
          resolvedProductAttrs[col.attributeCode!] = match.name;
        }
      } else if (col.optionsSource === 'fixed' && col.fixedOptions) {
        const hit = col.fixedOptions.find((o) => o.trim().toLowerCase() === raw.toLowerCase());
        if (!hit) {
          groupErrors.push({
            row: first.rowNum,
            column: col.label,
            message: `"${raw}" is not valid for "${col.label}". Valid options: ${col.fixedOptions.join(', ')}.`,
          });
        } else {
          resolvedProductAttrs[col.attributeCode!] = hit;
        }
      } else {
        resolvedProductAttrs[col.attributeCode!] = raw;
      }
    }

    // Non-first rows in a code-typed group must not redeclare conflicting product-level fields —
    // only the first row's product-level values are used, but a differing Name on a later row
    // is very likely a mistake, so warn (non-blocking).
    if (g.typedCode) {
      g.rows.slice(1).forEach((r) => {
        if (r.name.trim() && r.name.trim().toLowerCase() !== first.name.trim().toLowerCase()) {
          warnings.push({
            row: r.rowNum,
            message: `Product Name differs from row ${first.rowNum} for the same Product Code "${g.typedCode}" — the first row's value will be used.`,
          });
        }
      });
    }

    if (groupErrors.length > 0) {
      errors.push(...groupErrors);
      continue;
    }

    const existing = g.typedCode ? existingByCode.get(g.typedCode) : undefined;
    const isUpdate = !!existing;

    // ── Variants ──
    const variants: ValidatedVariant[] = [];
    let anyVariantRowErrored = false;

    for (const r of g.rows) {
      const rowErrors: RowError[] = [];

      if (!r.size.trim())
        rowErrors.push({ row: r.rowNum, column: 'Size', message: 'Size is required.' });

      const cost = parseNonNegNumber(r.default_cost);
      if (cost === null)
        rowErrors.push({
          row: r.rowNum,
          column: 'Cost Price',
          message: 'Cost Price must be a number 0 or more.',
        });

      const price = parseNonNegNumber(r.default_price);
      if (price === null)
        rowErrors.push({
          row: r.rowNum,
          column: 'Sell Price',
          message: 'Sell Price must be a number 0 or more.',
        });
      else if (price === 0)
        warnings.push({ row: r.rowNum, message: 'Sell Price is ₹0 — is this intentional?' });

      const openingStockRaw = r.opening_stock.trim();
      const openingStock = openingStockRaw ? parseNonNegInt(openingStockRaw) : 0;
      if (openingStockRaw && openingStock === null)
        rowErrors.push({
          row: r.rowNum,
          column: 'Opening Stock',
          message: 'Opening Stock must be a whole number 0 or more.',
        });

      const lowStockRaw = r.low_stock_level.trim();
      const lowStock = lowStockRaw ? parseNonNegInt(lowStockRaw) : 5;
      if (lowStockRaw && lowStock === null)
        rowErrors.push({
          row: r.rowNum,
          column: 'Low Stock Alert',
          message: 'Low Stock Alert must be a whole number 0 or more.',
        });

      const variantAttrColumns = columns.filter(
        (c) => c.kind === 'attribute' && c.level === 'variant',
      );
      const resolvedVariantAttrs: Record<string, string> = {};
      for (const col of variantAttrColumns) {
        const raw = (r.variantAttrs[col.attributeCode!] ?? '').trim();
        if (!raw) {
          if (col.required)
            rowErrors.push({
              row: r.rowNum,
              column: col.label,
              message: `"${col.label}" is required for this product type.`,
            });
          continue;
        }
        if (col.optionsSource === 'fixed' && col.fixedOptions) {
          const hit = col.fixedOptions.find((o) => o.trim().toLowerCase() === raw.toLowerCase());
          if (!hit)
            rowErrors.push({
              row: r.rowNum,
              column: col.label,
              message: `"${raw}" is not valid for "${col.label}". Valid options: ${col.fixedOptions.join(', ')}.`,
            });
          else resolvedVariantAttrs[col.attributeCode!] = hit;
        } else {
          resolvedVariantAttrs[col.attributeCode!] = raw;
        }
      }

      if (r.sku && (skuCount.get(r.sku) ?? 0) > 1) {
        rowErrors.push({
          row: r.rowNum,
          column: 'SKU',
          message: `SKU "${r.sku}" appears more than once in this file.`,
        });
      }

      const existingVariant = r.sku ? existingVariantBySku.get(r.sku) : undefined;
      let variantMatch: 'new' | 'update' = 'new';
      if (existingVariant) {
        if (isUpdate && existingVariant.product_id !== existing!.id) {
          rowErrors.push({
            row: r.rowNum,
            column: 'SKU',
            message: `SKU "${r.sku}" already exists under a different product.`,
          });
        } else if (!isUpdate) {
          rowErrors.push({
            row: r.rowNum,
            column: 'SKU',
            message: `SKU "${r.sku}" already exists but Product Code was left blank/new — cannot attach an existing SKU to a new product.`,
          });
        } else if (
          existingVariant.size.trim().toLowerCase() !== r.size.trim().toLowerCase() ||
          (existingVariant.color ?? '').trim().toLowerCase() !== r.color.trim().toLowerCase()
        ) {
          rowErrors.push({
            row: r.rowNum,
            column: 'SKU',
            message: `SKU "${r.sku}" already exists with a different Size/Color — import cannot change a SKU's identity.`,
          });
        } else {
          variantMatch = 'update';
        }
      }

      if (rowErrors.length > 0) {
        errors.push(...rowErrors);
        anyVariantRowErrored = true;
        continue;
      }

      variants.push({
        match: variantMatch,
        existing_id: existingVariant?.id,
        sku: existingVariant?.sku || r.sku, // Preserve established identifiers verbatim.
        size: r.size,
        color: r.color || undefined,
        default_cost: cost!,
        default_price: price!,
        low_stock_level: lowStock!,
        attributes: resolvedVariantAttrs,
        opening_stock: openingStock ?? 0,
        sourceRow: r.rowNum,
        ignoredOpeningStock: variantMatch === 'update' && (openingStock ?? 0) > 0,
      });
    }

    if (variants.length === 0) {
      if (!anyVariantRowErrored) {
        errors.push({ row: first.rowNum, message: `Product "${first.name}" has no variant rows.` });
      }
      continue;
    }

    const diffs: FieldDiff[] = [];
    if (existing) {
      if (existing.name !== first.name)
        diffs.push({ field: 'Name', oldValue: existing.name, newValue: first.name });
      if ((existing.description ?? '') !== first.description)
        diffs.push({
          field: 'Description',
          oldValue: existing.description ?? '',
          newValue: first.description,
        });
      if ((existing.category_id ?? '') !== (category_id ?? ''))
        diffs.push({
          field: 'Category',
          oldValue: existing.category_id ? '(set)' : '(none)',
          newValue: category_id ? first.category : '(none)',
        });
    }

    const group: ValidatedProductGroup = {
      group_key: '',
      match: isUpdate ? 'update' : 'new',
      existing_id: existing?.id,
      expected_updated_at: existing?.updated_at,
      product_code: existing?.product_code || g.typedCode || '', // Existing casing/characters are preserved.
      name: first.name,
      category_id,
      target_audience_id,
      age_group_id,
      description: first.description || undefined,
      attributes: resolvedProductAttrs,
      variants,
      diffs,
      isInactiveExisting: !!existing && !existing.is_active,
      sourceRows: g.rows.map((r) => r.rowNum),
    };

    if (
      options?.jerseyIdentifiers &&
      ((!isUpdate && !!g.typedCode) || variants.some((v) => v.match === 'new' && !!v.sku))
    ) {
      g.rows.forEach((r) =>
        errors.push({
          row: r.rowNum,
          message:
            'Leave new jersey product codes and SKUs blank; the server assigns them. Existing identifiers remain valid references.',
        }),
      );
      continue;
    }
    if (!isUpdate && !g.typedCode && !options?.jerseyIdentifiers) {
      newGroupsNeedingCode.push({ group, prefixInput: first });
    }

    validGroups.push(group);
  }

  await Promise.all(
    validGroups.map(async (group) => {
      group.group_key = await importGroupKey(group);
    }),
  );

  // ── Batch-safe product_code generation for new, code-less groups (FR-006) ──
  if (newGroupsNeedingCode.length > 0) {
    const prefixByGroup = newGroupsNeedingCode.map(({ group, prefixInput }) => {
      const prefix = buildProductCodePrefix({
        productTypeName: productType.name,
        teamName: prefixInput.productAttrs.TEAM || (group.attributes.TEAM as string | undefined),
        edition:
          (group.attributes.EDITION as string | undefined) || prefixInput.productAttrs.EDITION,
        sleeve:
          (group.attributes.SLEEVE_TYPE as string | undefined) ||
          prefixInput.productAttrs.SLEEVE_TYPE,
        collar:
          (group.attributes.COLLAR_TYPE as string | undefined) ||
          prefixInput.productAttrs.COLLAR_TYPE,
        player:
          (group.attributes.PLAYER_NAME as string | undefined) ||
          prefixInput.productAttrs.PLAYER_NAME,
        playerNumber:
          (group.attributes.PLAYER_NUMBER as string | undefined) ||
          prefixInput.productAttrs.PLAYER_NUMBER,
        productName: group.name,
      });
      return { group, prefix };
    });

    const uniquePrefixes = Array.from(new Set(prefixByGroup.map((x) => x.prefix)));
    const nextSeq: Record<string, number> = {};
    await Promise.all(
      uniquePrefixes.map(async (prefix) => {
        if (!prefix || prefix === 'KQ') {
          nextSeq[prefix] = 1;
          return;
        }
        try {
          const { data } = await supabase
            .from('products')
            .select('product_code')
            .eq('organization_id', orgId)
            .ilike('product_code', `${prefix}%`);
          let maxSeq = 0;
          (data ?? []).forEach((p: { product_code: string }) => {
            const parts = p.product_code.split('-');
            const num = parseInt(parts[parts.length - 1] ?? '', 10);
            if (!isNaN(num) && num > maxSeq) maxSeq = num;
          });
          nextSeq[prefix] = maxSeq + 1;
        } catch {
          nextSeq[prefix] = 1;
        }
      }),
    );

    for (const { group, prefix } of prefixByGroup) {
      const seq = nextSeq[prefix] ?? 1;
      nextSeq[prefix] = seq + 1;
      group.product_code = formatProductCode(prefix, seq);
    }
  }

  // ── Finalize variant SKUs now that every product_code is known ──
  const seenGeneratedSkus = new Set<string>();
  for (const g of validGroups) {
    for (const v of g.variants) {
      if (!v.sku && !options?.jerseyIdentifiers) {
        let generated = buildVariantSku(g.product_code, v.color, v.size);
        let suffix = 1;
        while (seenGeneratedSkus.has(generated) || existingVariantBySku.has(generated)) {
          generated = `${buildVariantSku(g.product_code, v.color, v.size)}-${suffix}`;
          suffix += 1;
        }
        seenGeneratedSkus.add(generated);
        v.sku = generated;
      }
    }
  }

  // ── Duplicate product_code across groups within the file ──
  const codeCount = new Map<string, number>();
  validGroups.forEach(
    (g) =>
      g.product_code && codeCount.set(g.product_code, (codeCount.get(g.product_code) ?? 0) + 1),
  );
  const finalGroups = validGroups.filter((g) => {
    if ((codeCount.get(g.product_code) ?? 0) > 1) {
      g.sourceRows.forEach((row) =>
        errors.push({
          row,
          message: `Product Code "${g.product_code}" is used by more than one product group in this file.`,
        }),
      );
      return false;
    }
    return true;
  });

  return {
    columns,
    groups: finalGroups,
    errors,
    warnings,
    totalDataRows: parsedRows.length,
  };
}

// ── Step 4: build the exact payload commit_bulk_product_import expects ──

export function buildCommitPayload(
  productTypeId: string,
  fileName: string,
  groups: ValidatedProductGroup[],
) {
  return {
    product_type_id: productTypeId,
    file_name: fileName,
    products: [...groups]
      .sort((a, b) => a.group_key.localeCompare(b.group_key))
      .map((g) => ({
        group_key: g.group_key,
        jersey_review: g.jersey_review,
        ...(g.jersey_entry ? { details: g.jersey_entry.details || {},
          name_intent: { mode: g.jersey_entry.name_mode || 'manual', manual_value: g.name },
          code_intent: { mode: g.jersey_entry.code_mode || (g.product_code ? 'manual' : 'auto'), manual_value: g.product_code },
        } : {}),
        match: g.match,
        existing_id: g.existing_id ?? null,
        expected_updated_at: g.expected_updated_at ?? null,
        product_code: g.product_code,
        name: g.name,
        category_id: g.category_id ?? null,
        target_audience_id: g.target_audience_id ?? null,
        age_group_id: g.age_group_id ?? null,
        description: g.description ?? null,
        attributes: g.attributes,
        variants: [...g.variants]
          .sort((a, b) =>
            stableJson([a.existing_id, a.sku, a.color, a.size]).localeCompare(
              stableJson([b.existing_id, b.sku, b.color, b.size]),
            ),
          )
          .map((v) => ({
            match: v.match,
            existing_id: v.existing_id ?? null,
            sku: v.sku,
            ...(g.jersey_entry ? { sku_intent: { mode: g.jersey_entry.sku_modes?.[v.sourceRow] || (v.sku ? 'manual' : 'auto'), manual_value: v.sku } } : {}),
            size: v.size,
            color: v.color ?? null,
            default_cost: v.default_cost,
            default_price: v.default_price,
            low_stock_level: v.low_stock_level,
            attributes: v.attributes,
            opening_stock: v.opening_stock,
          })),
      })),
  };
}

// Re-exported so the preview UI can show a color abbreviation preview if desired.
export { getAbbreviation, COLOR_CODES };
